import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Users, Plus, Edit, Trash2, Loader, UserPlus,
  LayoutGrid, Table as TableIcon, Search,
  User
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Drawer from '@/layout/Drawer'
import ConfirmModal from '@/widgets/ConfirmModal'
import GroupMembersModal from '@/widgets/GroupMembersModal'
import { getGroups, saveGroup, deleteGroup } from '@/api/kuizy'
import { formatDateTime } from '@/settings/master'
import ViewSwitcher from '../../components/ui/ViewSwitcher'

const empty = { id: 0, code: '', name: '', description: '' }

export default function GroupListPage() {
  const uid = localStorage.getItem('userId') || '1'
  const [rows, setRows] = useState([])
  const [loading, setLoad] = useState(true)

  const [view, setView] = useState('table')
  const [searchIn, setSI] = useState('')
  const [searchTxt, setST] = useState('')

  const [drawer, setDrawer] = useState(false)
  const [draft, setDraft] = useState(empty)
  const [saving, setSav] = useState(false)

  const [del, setDel] = useState(null)
  const [delLoad, setDL] = useState(false)

  const [member, setMem] = useState({ open: false, groupId: null, groupName: '', setTab: 'members' })
  const [alert, setAlert] = useState({ open: false, msg: '' })

  const load = useCallback(() => {
    setLoad(true)
    getGroups()
      .then(r => setRows(Array.isArray(r.data) ? r.data : r.data?.data ?? []))
      .catch(() => setAlert({ open: true, msg: 'โหลดข้อมูลไม่สำเร็จ' }))
      .finally(() => setLoad(false))
  }, [])
  useEffect(load, [load])

  const list = useMemo(() => rows.filter(g =>
    !searchTxt ||
    g.name.toLowerCase().includes(searchTxt.toLowerCase()) ||
    g.code.toLowerCase().includes(searchTxt.toLowerCase())
  ), [rows, searchTxt])

  const save = async e => {
    e.preventDefault()
    if (saving) return
    setSav(true)
    try { await saveGroup({ ...draft, learner_id: uid }); setDrawer(false); load() }
    catch { setAlert({ open: true, msg: 'บันทึกไม่สำเร็จ' }) }
    setSav(false)
  }

  const confirmDel = async () => {
    if (!del) return
    setDL(true)
    try { await deleteGroup(del.id); load() }
    catch { setAlert({ open: true, msg: 'ไม่สามารถลบได้' }) }
    setDL(false); setDel(null)
  }

  return (
    <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35 }} className="flex flex-col gap-6 p-4 sm:p-6">

      {/* header */}
      <motion.header initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, delay: .05 }} className="flex flex-wrap items-end gap-3 justify-between">
        <h1 onClick={load} className="flex items-center gap-2 text-lg font-semibold cursor-pointer">
          <Users size={20} /> จัดการกลุ่มผู้เรียน ({rows.length})
        </h1>

        <div className="flex flex-wrap gap-2">
          <ViewSwitcher view={view} setView={setView} />
          <button disabled={saving} onClick={() => { setDraft(empty); setDrawer(true) }}
            className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
            {saving ? <Loader size={16} className="animate-spin" /> : <Plus size={16} />} เพิ่มกลุ่มผู้เรียน
          </button>
        </div>
      </motion.header>

      {/* search */}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
          <div className="relative">
            <input value={searchIn} onChange={e => setSI(e.target.value)} onKeyDown={e => e.key === 'Enter' && setST(searchIn)}
              className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
              placeholder="ค้นหาชื่อกลุ่มผู้เรียน / ชื่อย่อกลุ่มผู้เรียน" />
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          </div>
        </div>
        <button onClick={() => setST(searchIn)}
          className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
          <Search size={16} /> ค้นหา
        </button>
      </motion.div>

      {/* card grid */}
      {view === 'card' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .35, delay: .1 }}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-12"><Loader className="animate-spin" /></div>
          ) : list.length === 0 ? (
            <div className="col-span-full text-center text-gray-500 py-12">– ไม่มีข้อมูล –</div>
          ) : list.map(g => (
            <motion.div key={g.id} whileHover={{ y: -4 }} className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{g.name}</p>
                  <p className="text-sm text-gray-500">{g.code}</p>
                </div>
                <div>
                  {/* <button onClick={() => setMem({ open: true, groupId: g.id, groupName: g.name, setTab: 'add' })}
                    className="text-green-600 hover:text-green-500 pr-2"><UserPlus size={18} /></button> */}
                  {(g.code != "member" ?
                    <button onClick={() => { setDraft(g); setDrawer(true) }} className="text-primary hover:text-primary/80"><Edit size={18} /></button> : null
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                <button onClick={() => setMem({ open: true, groupId: g.id, groupName: g.name, setTab: 'members' })}
                  className="bg-primary/10 rounded-lg p-3 flex flex-col items-center hover:bg-neutral-200">
                  <User size={18} className="text-secondary" />
                  <span className="text-[10px] text-gray-600 mt-1">ผู้เรียน</span>
                  <strong className="text-lg">{g.member_count ?? 0}</strong>
                </button>
              </div>

              <div className="flex items-end justify-between">
                <div className="text-xs text-gray-500">
                  <p>สร้างเมื่อ {formatDateTime(g.created_at)}</p>
                  <p>แก้ไขล่าสุด {formatDateTime(g.updated_at || g.created_at)}</p>
                </div>
                {(g.code != "member" ?
                  <button onClick={() => setDel(g)} className="text-red-600 hover:text-red-500"><Trash2 size={18} /></button> : null
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* table */}
      {view === 'table' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .35, delay: .1 }}
          className="bg-white rounded-xl shadow-md overflow-x-auto">
          <table className="min-w-[760px] w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
              <tr>
                <th className="py-2 px-3 w-12 text-left">#</th>
                <th className="py-2 px-3 text-left">ชื่อย่อกลุ่มผู้เรียน</th>
                <th className="py-2 px-3 text-left">ชื่อกลุ่มผู้เรียน</th>
                <th className="py-2 px-3 text-center">ผู้เรียน</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">สร้างเมื่อ</th>
                <th className="py-2 px-3 text-right hidden md:table-cell">แก้ไขล่าสุด</th>
                <th className="py-2 px-3 w-32 text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-6 text-center"><Loader className="animate-spin" /></td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={7} className="py-6 text-center text-gray-500">– ไม่มีข้อมูล –</td></tr>
              ) : list.map((g, i) => (
                <tr key={g.id} className="odd:bg-white even:bg-gray-50 whitespace-nowrap">
                  <td className="py-2 px-3">{i + 1}</td>
                  <td className="py-2 px-3">{g.code}</td>
                  <td className="py-2 px-3">{g.name}</td>
                  <td className="py-2 px-3 text-center">
                    <button onClick={() => setMem({ open: true, groupId: g.id, groupName: g.name, setTab: 'members' })}
                      className="text-primary font-bold hover:underline">{g.member_count ?? 0}</button>
                  </td>
                  <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(g.created_at)}</td>
                  <td className="py-2 px-3 text-right hidden md:table-cell">{formatDateTime(g.updated_at)}</td>
                  <td className="py-2 px-3 text-right space-x-2">
                    <button onClick={() => setMem({ open: true, groupId: g.id, groupName: g.name, setTab: 'add' })}
                      className="text-green-600 hover:text-green-600/80" title="เพิ่มผู้เรียน"><UserPlus size={16} /></button>
                    <button onClick={() => { setDraft(g); setDrawer(true) }} className="text-primary hover:text-primary/80" title="แก้ไข"><Edit size={16} /></button>
                    {(g.code != "member" ? <button onClick={() => setDel(g)} className="text-red-600 hover:text-red-500" title="ลบ"><Trash2 size={16} /></button> : null)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {/* drawer */}
      <Drawer open={drawer} title={draft.id ? 'แก้ไขกลุ่มผู้เรียน' : 'เพิ่มกลุ่มผู้เรียน'} onClose={() => setDrawer(false)}>
        <form onSubmit={save} className="flex flex-col gap-4">
          <label className="flex items-center gap-1 text-sm"><Users size={14} /> ชื่อกลุ่มผู้เรียน <span className="text-red-500 font-bold">*</span></label>
          <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
          <label className="flex items-center gap-1 text-sm"><Users size={14} /> ชื่อย่อกลุ่มผู้เรียน <span className="text-red-500 font-bold">*</span></label>
          <input value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
          <button disabled={saving || !draft.code.trim() || !draft.name.trim()}
            className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 disabled:opacity-50">
            {saving ? <Loader size={16} className="animate-spin" /> : 'บันทึก'}
          </button>
        </form>
      </Drawer>

      {/* modals & alerts */}
      <AnimatePresence>
        {member.open && <GroupMembersModal groupId={member.groupId} groupName={member.groupName} setTabs={member.setTab} onClose={() => { setMem({ open: false }); load(); }} />}
        {del && <ConfirmModal type="confirm" title="ยืนยันการลบ" message={`ลบกลุ่มผู้เรียน "${del.name}" ?`} onConfirm={confirmDel} onCancel={() => setDel(null)} loading={delLoad} />}
        {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: '' })} />}
      </AnimatePresence>
    </motion.section>
  )
}
